import React from 'react';
import ModernCTA from '../../components/ModernCTA';

const CloudInfrastructure = () => {
  return (
    <div style={styles.container}>
      {/* Hero Section */}
      <section style={styles.hero}>
        <div style={styles.heroContent}>
          <h1 style={styles.heroTitle}>Cloud Infrastructure Solutions</h1>
          <p style={styles.heroSubtitle}>
            Enterprise-grade AWS/GCP architectures with Kubernetes orchestration and infrastructure-as-code for scalable, resilient systems.
          </p>
        </div>
        <div style={styles.cloudIllustration}>
          <div style={styles.cloud}></div>
          <div style={styles.cloud}></div>
          <div style={styles.serverCluster}></div>
        </div>
      </section>

      {/* Services Section */}
      <section style={styles.services}>
        <div style={styles.sectionHeader}>
          <span style={styles.sectionTag}>SERVICES</span>
          <h2 style={styles.sectionTitle}>Cloud Architecture Expertise</h2>
          <p style={styles.sectionDescription}>
            We design and implement cloud-native solutions tailored to your workload requirements.
          </p>
        </div>

        <div style={styles.serviceGrid}>
          <div style={styles.serviceCard}>
            <div style={styles.serviceIcon}>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 3v12"></path>
                <circle cx="18" cy="6" r="3"></circle>
                <circle cx="6" cy="18" r="3"></circle>
                <path d="M18 9a9 9 0 0 1-9 9"></path>
              </svg>
            </div>
            <h3 style={styles.serviceTitle}>AWS Architecture</h3>
            <p style={styles.serviceText}>
              Certified AWS solutions with VPC design, security groups, IAM policies, and optimized service selection (EC2, RDS, Lambda, S3, etc.).
            </p>
            <ul style={styles.serviceList}>
              <li style={styles.serviceListItem}>Multi-AZ deployments</li>
              <li style={styles.serviceListItem}>Cost-optimized resource allocation</li>
              <li style={styles.serviceListItem}>Disaster recovery planning</li>
            </ul>
          </div>

          <div style={styles.serviceCard}>
            <div style={styles.serviceIcon}>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="2" y1="12" x2="22" y2="12"></line>
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
              </svg>
            </div>
            <h3 style={styles.serviceTitle}>GCP Architecture</h3>
            <p style={styles.serviceText}>
              Google Cloud Platform implementations with Compute Engine, Cloud SQL, Kubernetes Engine, and integrated data analytics services.
            </p>
            <ul style={styles.serviceList}>
              <li style={styles.serviceListItem}>Global load balancing</li>
              <li style={styles.serviceListItem}>BigQuery integration</li>
              <li style={styles.serviceListItem}>AI/ML pipeline setup</li>
            </ul>
          </div>

          <div style={styles.serviceCard}>
            <div style={styles.serviceIcon}>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
                <line x1="12" y1="22.08" x2="12" y2="12"></line>
              </svg>
            </div>
            <h3 style={styles.serviceTitle}>Kubernetes Orchestration</h3>
            <p style={styles.serviceText}>
              Production-grade Kubernetes clusters with Helm charts, operators, service meshes (Istio/Linkerd), and auto-scaling configurations.
            </p>
            <ul style={styles.serviceList}>
              <li style={styles.serviceListItem}>Multi-cluster management</li>
              <li style={styles.serviceListItem}>CI/CD pipeline integration</li>
              <li style={styles.serviceListItem}>Stateful workload support</li>
            </ul>
          </div>
        </div>
      </section>

      {/* IaC Section */}
      <section style={styles.iacSection}>
        <div style={styles.iacContent}>
          <div style={styles.iacText}>
            <span style={styles.sectionTag}>DEVOPS</span>
            <h2 style={styles.sectionTitle}>Infrastructure-as-Code</h2>
            <p style={styles.iacDescription}>
              We implement immutable infrastructure using industry-standard tools for version-controlled, repeatable deployments.
            </p>
            <div style={styles.iacTools}>
              <div style={styles.iacTool}>
                <div style={styles.toolIcon}>T</div>
                <div>
                  <h4 style={styles.toolTitle}>Terraform</h4>
                  <p style={styles.toolDesc}>Cloud-agnostic provisioning with modules and workspaces</p>
                </div>
              </div>
              <div style={styles.iacTool}>
                <div style={styles.toolIcon}>A</div>
                <div>
                  <h4 style={styles.toolTitle}>Ansible</h4>
                  <p style={styles.toolDesc}>Configuration management and orchestration</p>
                </div>
              </div>
              <div style={styles.iacTool}>
                <div style={styles.toolIcon}>P</div>
                <div>
                  <h4 style={styles.toolTitle}>Pulumi</h4>
                  <p style={styles.toolDesc}>Infrastructure as real code using familiar languages</p>
                </div>
              </div>
            </div>
          </div>
          <div style={styles.iacVisual}>
            <div style={styles.codeWindow}>
              <div style={styles.codeHeader}>
                <div style={styles.codeDots}></div>
                <div style={styles.codeDots}></div>
                <div style={styles.codeDots}></div>
              </div>
              <pre style={styles.codeBlock}>
                <code style={styles.codeText}>
{`resource "aws_vpc" "main" {
  cidr_block = "10.0.0.0/16"

  tags = {
    Name = "production-vpc"
  }
}

module "eks_cluster" {
  source = "terraform-aws-modules/eks/aws"
  
  cluster_name    = "prod-cluster"
  cluster_version = "1.21"
  subnets         = aws_subnet.public.*.id
  
  node_groups = {
    workers = {
      desired_capacity = 3
      max_capacity     = 5
      min_capacity     = 1
      
      instance_type = "m5.large"
    }
  }
}`}
                </code>
              </pre>
            </div>
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section style={styles.caseStudies}>
        <div style={styles.sectionHeader}>
          <span style={styles.sectionTag}>CASE STUDIES</span>
          <h2 style={styles.sectionTitle}>Infrastructure Transformations</h2>
        </div>

        <div style={styles.studyGrid}>
          <div style={styles.studyCard}>
            <div style={styles.studyHeader}>
              <div style={styles.studyLogo}></div>
              <h3 style={styles.studyTitle}>E-Commerce Platform</h3>
            </div>
            <p style={styles.studyText}>
              Migrated from on-premise to AWS with Kubernetes, achieving 99.99% uptime and reducing infrastructure costs by 40%.
            </p>
            <div style={styles.studyStats}>
              <div style={styles.statItem}>
                <span style={styles.statNumber}>99.99%</span>
                <span style={styles.statLabel}>Uptime</span>
              </div>
              <div style={styles.statItem}>
                <span style={styles.statNumber}>40%</span>
                <span style={styles.statLabel}>Cost Reduction</span>
              </div>
            </div>
          </div>

          <div style={styles.studyCard}>
            <div style={styles.studyHeader}>
              <div style={styles.studyLogo}></div>
              <h3 style={styles.studyTitle}>SaaS Provider</h3>
            </div>
            <p style={styles.studyText}>
              Implemented GCP with Terraform across 3 regions, enabling automatic scaling during 10x traffic spikes.
            </p>
            <div style={styles.studyStats}>
              <div style={styles.statItem}>
                <span style={styles.statNumber}>3</span>
                <span style={styles.statLabel}>Regions</span>
              </div>
              <div style={styles.statItem}>
                <span style={styles.statNumber}>10x</span>
                <span style={styles.statLabel}>Traffic Handling</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <ModernCTA />
    </div>
  );
};

const styles = {
  container: {
    marginTop: '110px',
    color: '#ffffff',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif",
    lineHeight: 1.6,
  },
  hero: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '60px 5%',
    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
    textAlign: 'center',
    '@media (min-width: 768px)': {
      flexDirection: 'row',
      textAlign: 'left',
      justifyContent: 'space-between',
      padding: '80px 5%',
    },
  },
  heroContent: {
    maxWidth: '600px',
    marginBottom: '40px',
    '@media (min-width: 768px)': {
      marginBottom: 0,
    },
  },
  heroTitle: {
    fontSize: '2.5rem',
    fontWeight: 800,
    lineHeight: 1.2,
    marginBottom: '20px',
    background: 'linear-gradient(90deg, #ffffff 0%, #cbd5e1 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    '@media (min-width: 768px)': {
      fontSize: '3.5rem',
      marginBottom: '24px',
    },
  },
  heroSubtitle: {
    fontSize: '1.1rem',
    color: '#94a3b8',
    marginBottom: '30px',
    maxWidth: '500px',
    '@media (min-width: 768px)': {
      fontSize: '1.25rem',
      marginBottom: '40px',
    },
  },
  cloudIllustration: {
    position: 'relative',
    width: '300px',
    height: '200px',
    '@media (min-width: 768px)': {
      width: '400px',
      height: '300px',
    },
  },
  cloud: {
    position: 'absolute',
    width: '80px',
    height: '40px',
    borderRadius: '40px',
    background: 'rgba(59, 130, 246, 0.2)',
    '@media (min-width: 768px)': {
      width: '120px',
      height: '60px',
      borderRadius: '60px',
    },
  },
  serverCluster: {
    position: 'absolute',
    width: '120px',
    height: '80px',
    bottom: '0',
    right: '0',
    background: 'radial-gradient(circle at center, rgba(59, 130, 246, 0.3) 0%, transparent 70%)',
    border: '1px solid rgba(59, 130, 246, 0.4)',
    borderRadius: '8px',
    '@media (min-width: 768px)': {
      width: '180px',
      height: '120px',
      borderRadius: '12px',
    },
  },
  services: {
    padding: '60px 5%',
    background: '#0f172a',
    '@media (min-width: 768px)': {
      padding: '100px 5%',
    },
  },
  sectionHeader: {
    textAlign: 'center',
    maxWidth: '700px',
    margin: '0 auto 40px',
    '@media (min-width: 768px)': {
      margin: '0 auto 60px',
    },
  },
  sectionTag: {
    display: 'inline-block',
    color: '#3b82f6',
    backgroundColor: 'rgba(59, 130, 246, 0.1)',
    padding: '6px 12px',
    borderRadius: '20px',
    fontSize: '0.875rem',
    fontWeight: 600,
    letterSpacing: '0.05em',
    marginBottom: '16px',
  },
  sectionTitle: {
    fontSize: '2rem',
    fontWeight: 700,
    marginBottom: '16px',
    lineHeight: 1.3,
    '@media (min-width: 768px)': {
      fontSize: '2.5rem',
    },
  },
  sectionDescription: {
    fontSize: '1rem',
    color: '#94a3b8',
    lineHeight: 1.7,
    '@media (min-width: 768px)': {
      fontSize: '1.125rem',
    },
  },
  serviceGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '24px',
    maxWidth: '1200px',
    margin: '0 auto',
    '@media (min-width: 600px)': {
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: '32px',
    },
  },
  serviceCard: {
    background: '#1e293b',
    borderRadius: '12px',
    padding: '30px',
    '@media (min-width: 768px)': {
      padding: '40px 32px',
    },
  },
  serviceIcon: {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    background: 'rgba(59, 130, 246, 0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '20px',
    color: '#3b82f6',
    '@media (min-width: 768px)': {
      width: '56px',
      height: '56px',
      marginBottom: '24px',
    },
  },
  serviceTitle: {
    fontSize: '1.25rem',
    fontWeight: 600,
    marginBottom: '12px',
    '@media (min-width: 768px)': {
      fontSize: '1.5rem',
      marginBottom: '16px',
    },
  },
  serviceText: {
    color: '#94a3b8',
    fontSize: '0.9375rem',
    lineHeight: 1.7,
    marginBottom: '20px',
    '@media (min-width: 768px)': {
      fontSize: '1rem',
      marginBottom: '24px',
    },
  },
  serviceList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
  },
  serviceListItem: {
    marginBottom: '10px',
    color: '#e2e8f0',
    fontSize: '0.9375rem',
    position: 'relative',
    paddingLeft: '20px',
    '@media (min-width: 768px)': {
      marginBottom: '12px',
      fontSize: '1rem',
      paddingLeft: '24px',
    },
  },
  iacSection: {
    padding: '60px 5%',
    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
    '@media (min-width: 768px)': {
      padding: '100px 5%',
    },
  },
  iacContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    maxWidth: '1200px',
    margin: '0 auto',
    gap: '40px',
    '@media (min-width: 1024px)': {
      flexDirection: 'row',
      gap: '60px',
    },
  },
  iacText: {
    flex: 1,
    width: '100%',
  },
  iacDescription: {
    fontSize: '1rem',
    color: '#94a3b8',
    margin: '20px 0 24px',
    lineHeight: 1.7,
    '@media (min-width: 768px)': {
      fontSize: '1.125rem',
      margin: '24px 0 32px',
    },
  },
  iacTools: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    '@media (min-width: 768px)': {
      gap: '24px',
    },
  },
  iacTool: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  toolIcon: {
    width: '40px',
    height: '40px',
    borderRadius: '8px',
    background: 'rgba(59, 130, 246, 0.1)',
    color: '#3b82f6',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    fontSize: '1rem',
    flexShrink: 0,
    '@media (min-width: 768px)': {
      width: '48px',
      height: '48px',
      fontSize: '1.25rem',
    },
  },
  toolTitle: {
    fontSize: '1.1rem',
    fontWeight: 600,
    margin: '0 0 4px 0',
    '@media (min-width: 768px)': {
      fontSize: '1.25rem',
    },
  },
  toolDesc: {
    color: '#94a3b8',
    fontSize: '0.875rem',
    margin: 0,
    '@media (min-width: 768px)': {
      fontSize: '0.9375rem',
    },
  },
  iacVisual: {
    flex: 1,
    width: '100%',
    marginTop: '30px',
    '@media (min-width: 1024px)': {
      marginTop: 0,
    },
  },
  codeWindow: {
    background: '#0f172a',
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
  },
  codeHeader: {
    height: '36px',
    background: '#1e293b',
    display: 'flex',
    alignItems: 'center',
    padding: '0 12px',
    gap: '6px',
    '@media (min-width: 768px)': {
      height: '40px',
      padding: '0 16px',
      gap: '8px',
    },
  },
  codeDots: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    background: '#3b82f6',
    '@media (min-width: 768px)': {
      width: '12px',
      height: '12px',
    },
  },
  codeBlock: {
    margin: 0,
    padding: '16px',
    overflowX: 'auto',
    '@media (min-width: 768px)': {
      padding: '24px',
    },
  },
  codeText: {
    color: '#e2e8f0',
    fontFamily: "'Fira Code', monospace",
    fontSize: '0.8125rem',
    lineHeight: 1.6,
    '@media (min-width: 768px)': {
      fontSize: '0.875rem',
    },
  },
  caseStudies: {
    padding: '60px 5%',
    background: '#0f172a',
    '@media (min-width: 768px)': {
      padding: '100px 5%',
    },
  },
  studyGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '24px',
    maxWidth: '1200px',
    margin: '0 auto',
    '@media (min-width: 768px)': {
      gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
      gap: '32px',
    },
  },
  studyCard: {
    background: '#1e293b',
    borderRadius: '12px',
    padding: '30px',
    '@media (min-width: 768px)': {
      padding: '40px',
    },
  },
  studyHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '20px',
    '@media (min-width: 768px)': {
      marginBottom: '24px',
    },
  },
  studyLogo: {
    width: '40px',
    height: '40px',
    borderRadius: '8px',
    background: 'rgba(59, 130, 246, 0.1)',
    marginRight: '12px',
    '@media (min-width: 768px)': {
      width: '48px',
      height: '48px',
      marginRight: '16px',
    },
  },
  studyTitle: {
    fontSize: '1.25rem',
    fontWeight: 600,
    margin: 0,
    '@media (min-width: 768px)': {
      fontSize: '1.5rem',
    },
  },
  studyText: {
    color: '#94a3b8',
    fontSize: '0.9375rem',
    lineHeight: 1.7,
    marginBottom: '24px',
    '@media (min-width: 768px)': {
      fontSize: '1rem',
      marginBottom: '32px',
    },
  },
  studyStats: {
    display: 'flex',
    gap: '20px',
    '@media (min-width: 768px)': {
      gap: '24px',
    },
  },
  statItem: {
    textAlign: 'center',
  },
  statNumber: {
    display: 'block',
    fontSize: '1.5rem',
    fontWeight: 700,
    color: '#3b82f6',
    lineHeight: 1.2,
    '@media (min-width: 768px)': {
      fontSize: '2rem',
    },
  },
  statLabel: {
    display: 'block',
    fontSize: '0.8125rem',
    color: '#94a3b8',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    '@media (min-width: 768px)': {
      fontSize: '0.875rem',
    },
  },
};

export default CloudInfrastructure;